import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Eye, MousePointer, DollarSign, Users, Target } from "lucide-react";

export default function CampaignMetricsGrid({ campaigns, isLoading, onCampaignClick }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-muted rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-4 bg-muted rounded"></div>
                <div className="h-4 bg-muted rounded w-5/6"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!campaigns || campaigns.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-12 text-center">
          <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">Nenhuma campanha encontrada</h3>
          <p className="text-muted-foreground">
            Sincronize seus dados do Facebook para ver as métricas
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatCurrency = (num) => {
    if (!num && num !== 0) return '$0';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20',
      paused: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
      completed: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
      archived: 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20',
    };
    return colors[status] || colors.archived;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {campaigns.map((campaign) => {
        const metrics = campaign.metrics || {};
        const cpl = metrics.cpl || 0;
        const ctr = metrics.ctr || 0;
        const spend = metrics.spend || 0;
        const impressions = metrics.impressions || 0;
        const clicks = metrics.clicks || 0;
        const leads = metrics.leads || 0;

        return (
          <Card 
            key={campaign.id} 
            className="hover:shadow-lg transition-all cursor-pointer hover:border-primary/50"
            onClick={() => onCampaignClick?.(campaign)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-lg font-semibold line-clamp-2 flex-1">
                  {campaign.campaign_name}
                </CardTitle>
                <Badge variant="outline" className={getStatusColor(campaign.status)}>
                  {campaign.status}
                </Badge>
              </div>
              {campaign.market && (
                <p className="text-sm text-muted-foreground">
                  {campaign.market.toUpperCase()}
                </p>
              )}
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Main Metrics */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <DollarSign className="h-3 w-3" />
                    <span className="text-xs">Gasto</span>
                  </div>
                  <p className="text-xl font-bold text-foreground">
                    {formatCurrency(spend)}
                  </p>
                </div>
                
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Users className="h-3 w-3" />
                    <span className="text-xs">Leads</span>
                  </div>
                  <p className="text-xl font-bold text-foreground">
                    {formatNumber(leads)}
                  </p>
                </div>
              </div>

              {/* Secondary Metrics */}
              <div className="space-y-2 pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Eye className="h-4 w-4" />
                    <span>Impressões</span>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {formatNumber(impressions)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MousePointer className="h-4 w-4" />
                    <span>Clicks</span>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {formatNumber(clicks)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Target className="h-4 w-4" />
                    <span>CTR</span>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {ctr.toFixed(2)}%
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <DollarSign className="h-4 w-4" />
                    <span>CPL</span>
                  </div>
                  <span className={`text-sm font-medium ${
                    cpl > 0 && cpl < 10 ? 'text-green-600 dark:text-green-400' :
                    cpl >= 10 && cpl < 20 ? 'text-yellow-600 dark:text-yellow-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(cpl)}
                  </span>
                </div>
              </div>

              {/* Performance Indicator */}
              {leads > 0 && (
                <div className="pt-2 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Performance</span>
                    {cpl < 15 ? (
                      <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                        <TrendingUp className="h-4 w-4" />
                        <span className="text-xs font-medium">Excelente</span>
                      </div>
                    ) : cpl < 30 ? (
                      <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                        <TrendingUp className="h-4 w-4" />
                        <span className="text-xs font-medium">Bom</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                        <TrendingDown className="h-4 w-4" />
                        <span className="text-xs font-medium">Requer atenção</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
